/**
 * P-3 日報詳細・入力: 「本日の勤務実績」セクション (2026-09-09、user Q1)。
 *
 * 日報 (店舗×営業日の単位) から、既存の `work_records` (勤務実績) を作成・修正できるようにする。
 * 提出・承認は既存のシフト実績画面 (`/shift/records`) で行う想定のため、ここでは
 * 作成・修正のみを扱う (user判断、§4-5型の「別システムの状態遷移を混ぜない」)。
 *
 * ⚠️ スコープ限定:
 * ① `work_records` は Full版限定機能 (`requireEdition('full')`) のため、本セクションも
 *    Full版でのみ表示する (Lite版には実績記録という概念自体が存在しない)。
 * ② 社員選択は「その店舗の全社員一覧」から選ぶのみ (シフト予定との突き合わせはしない、
 *    user判断)。`GET /daily-reports/work-roster` は shift:update が self scope の人には空配列を
 *    返すため、その場合は自分の分のみ記録できる (ピッカー自体を出さない)。
 * ③ 業務ロジック (重複時間帯チェック・休憩自動控除・本人入力可否等) は複製しない。
 *    `WorkRecordModal` (`@/components/WorkRecordModal.tsx`、`/shift/records` と共有) が
 *    既存の `POST/PUT /work-records` をそのまま呼ぶ。
 *
 * v0.4後半 月次実績一括入力 第2フェーズ I-7 (§10、D-1029/D-1015) での改修:
 * ・候補ロースターを `GET /work-records/roster` (主所属のみ、月次入力と共有) から
 *   `GET /daily-reports/work-roster` (主所属∪兼務∪応援候補、日報専用) へ切替えた。
 * ・`daily_report_work_section_visible` (店舗単位で表示ON/OFF、既定ON) が false の店舗では
 *   見出しごとセクション自体を出さない。
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, PenLine } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { isoToLocalHm, buildLocalIso, type PersonRef } from '@/lib/shift-format';
import { WorkRecordModal, StatusBadge, type WorkRecord, type WorkRecordModalTarget } from '@/components/WorkRecordModal';

type RosterEntry = { employee_id: string; name: string };
type ResolvedWorkSectionVisible = { value: boolean | null };

const SELF_OPTION = '__self__';

export function DailyReportWorkShiftsSection({ nodeId, businessDate }: { nodeId: string; businessDate: string }) {
  const edition = useAuthStore((s) => s.user?.edition);
  const qc = useQueryClient();
  const [target, setTarget] = useState<WorkRecordModalTarget | null>(null);
  // design-and-codeレビュー HIGH是正 (2026-09-09): roster (店舗の在籍者一覧) が1件以上あるとき
  // (=manager scope) は「自分」への既定選択を持たない。自分自身が社員として存在しない
  // actor (company_admin等) が既定のまま押すと、バックエンドが必ず
  // 「employee_id または external_person_id を指定してください」を返す (実機で再現)。
  // roster が0件 (self scopeのみ) のときだけ暗黙に自分を対象にする (§6d、既存の主要ユースケース)。
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');

  const enabled = edition === 'full';
  // I-7 (D-1029): 店舗単位の表示設定。既定はON (未設定=true) のため、取得前は「非表示と決めつけない」。
  const visibilityQuery = useQuery<ResolvedWorkSectionVisible>({
    queryKey: ['daily-report-work-section-visible', nodeId],
    queryFn: () => apiFetch(`/inheritable-settings/daily_report_work_section_visible/resolve?node_id=${nodeId}`),
    enabled,
  });
  const sectionVisible = visibilityQuery.data ? visibilityQuery.data.value !== false : true;

  const recordsQueryKey = ['daily-report-work-records', nodeId, businessDate];
  const recordsQuery = useQuery<{ work_records: WorkRecord[] }>({
    queryKey: recordsQueryKey,
    queryFn: () => apiFetch(`/work-records?node_id=${nodeId}&from=${businessDate}&to=${businessDate}`),
    enabled: enabled && sectionVisible,
  });
  const rosterQuery = useQuery<RosterEntry[]>({
    queryKey: ['daily-report-work-roster', nodeId],
    queryFn: () => apiFetch(`/daily-reports/work-roster?node_id=${nodeId}`),
    enabled: enabled && sectionVisible,
  });

  if (!enabled || !sectionVisible) return null;

  const records = recordsQuery.data?.work_records ?? [];
  const roster = rosterQuery.data ?? [];

  const handleDone = () => {
    setTarget(null);
    void qc.invalidateQueries({ queryKey: recordsQueryKey });
  };

  // roster ありのときは選択必須 (未選択のまま「自分」を暗黙に送らない)、roster 無しのときは
  // 常に自分が対象 (self scope しか無いユーザーの主要ユースケース)。
  const canCreate = roster.length === 0 || selectedEmployeeId !== '';

  const openCreate = () => {
    if (!canCreate) return;
    const person: PersonRef | null = (roster.length === 0 || selectedEmployeeId === SELF_OPTION)
      ? null : { employee_id: selectedEmployeeId, external_person_id: null };
    setTarget({
      mode: 'create', workDate: businessDate, plannedAssignmentId: null,
      homeNodeId: nodeId, workNodeId: nodeId, person,
      defaultStartAt: buildLocalIso(businessDate, '09:00'), defaultEndAt: buildLocalIso(businessDate, '17:00'),
    });
  };

  return (
    <section className="py-3 border-t border-surface-line">
      <h2 className="text-label text-text-sub mb-2">本日の勤務実績</h2>
      {/* QAレビューMEDIUM是正: 403 (shift:view のスコープがこの店舗を含まない) を isError で
          見ずに空配列扱いすると、権限が無いだけなのに「まだ記録がありません」と誤って伝わる。 */}
      {recordsQuery.isError ? (
        <p className="text-caption text-danger-text mb-2" role="alert">この店舗の勤務実績を閲覧する権限がありません。</p>
      ) : recordsQuery.isLoading ? (
        <p className="text-caption text-text-sub">読み込み中…</p>
      ) : records.length === 0 ? (
        <p className="text-caption text-text-sub mb-2">まだ記録がありません。</p>
      ) : (
        <ul className="space-y-1.5 mb-2">
          {records.map((r) => (
            <li key={r.work_record_id} className="flex items-center justify-between gap-2 flex-wrap text-body">
              <span className="text-text-body">
                {r.display_name ?? '(氏名不明)'}
                {r.record_kind === 'worked' && (
                  <span className="text-text-sub ml-2 tabular-nums">{isoToLocalHm(r.start_at)}〜{isoToLocalHm(r.end_at)}</span>
                )}
                {r.record_kind === 'absence' && <span className="text-text-sub ml-2">欠勤</span>}
              </span>
              <span className="flex items-center gap-2">
                <StatusBadge status={r.status} />
                {(r.status === 'draft' || r.status === 'rejected') && r.record_kind === 'worked' && (
                  <button type="button" aria-label="実績を修正"
                    className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-text-sub hover:text-text-body focus-ring"
                    onClick={() => setTarget({ mode: 'edit', record: r })}>
                    <PenLine size={14} aria-hidden />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {roster.length > 0 && (
          <Select value={selectedEmployeeId || undefined} onValueChange={setSelectedEmployeeId}>
            <SelectTrigger aria-label="対象者" className="w-40 min-h-[44px]"><SelectValue placeholder="対象者を選択" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={SELF_OPTION}>自分</SelectItem>
              {roster.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} disabled={!canCreate} onClick={openCreate}>実績を記録</Button>
      </div>
      {target && <WorkRecordModal target={target} onClose={() => setTarget(null)} onDone={handleDone} />}
    </section>
  );
}
